<template>
  <div :style="aspectRatio" :class="headerClassValue" :id="`uuid-${$attrs.uuid}`">
    <div class="hero-single-content__container">
      <div class="container__wrapper">
        <div class="hero-single-content__items">
          <div v-if="isHeroContentImage" class="hero-single-content__background-image">
            <picture v-if="isMobile && (!mediaMobile || !mediaMobile.url)">
              <source
                  media="(min-width:0) and (max-width:640px)"
                  :srcset="media.url"
                  v-if="media.url"
              />
              <pg-image
                  :src="media.url"
                  alt="hero-single-image"
              />
            </picture>
            <picture v-else-if="isMobile && mediaMobile && mediaMobile.url">
              <source
                  media="(min-width:0) and (max-width:640px)"
                  :srcset="mediaMobile.url"
                  v-if="mediaMobile.url !== '' && isMobile"
              />
              <pg-image
                  :src="mediaMobile.url"
                  :alt="imageAlt"
              />
            </picture>
            <picture v-else>
              <source
                  media="(min-width:641)"
                  :srcset="media.url"
                  v-if="media.url"
              />
              <pg-image
                  :src="media.url"
                  :alt="imageAlt"
              />
            </picture>
          </div>
          <div v-if="isVideoContent" :class="[{'vimeo-controls': vimeoControls}, 'hero-single-content__background-video']">
            <video v-if="isMgnlVideo"
                   class="mgnl-video"
                   ref="videoPlayer"
                   :autoplay="autoplayVideo"
                   :muted="true"
                   loop
                   :poster="previewImage"
              <source :src="videoSource" type="video/mp4">
            </video>
            <vue-vimeo-player v-if="isVimeoVideo"
                              @error="handlePlayerError"
                              :class="vimeoPlayerClass"
                              @ready=true
                              ref="videoPlayer"
                              :video-id="vimeoVideoSource"
                              :autoplay="autoplayVideo"
                              :options="!vimeoControls ? options : ''"
                              :controls="vimeoControls"
                              :player-height="'100%'"
                              :player-width="'100%'"
                              :poster="previewImage"
                              :aria-label="$msg(`common.heroheading.video.container.aria`)"
            ></vue-vimeo-player>
          </div>
          <div :class="`hero-single-content__overlay ${ overlay }`" v-if="!vimeoControls"></div>
          <div v-if="isMobile && isVideoContent && !vimeoControls && mediaMobile.type !== 'image'" class="toggle-play">
            <button @click="toggleVideoPlayback" type="button" :aria-label="isVideoPlaying ? $msg('common.heroheading.video.pause.aria') : $msg('common.heroheading.video.play.aria')">
              <i class="icon" :class="{ 'icon-pause': isVideoPlaying, 'icon-play': !isVideoPlaying }"></i>
            </button>
          </div>
          <div v-else-if="!isMobile && isVideoContent && !vimeoControls" class="toggle-play">
            <button @click="toggleVideoPlayback" type="button" :aria-label="isVideoPlaying ? $msg('common.heroheading.video.pause.aria') : $msg('common.heroheading.video.play.aria')">
              <i class="icon" :class="{ 'icon-pause': isVideoPlaying, 'icon-play': !isVideoPlaying }"></i>
            </button>
          </div>
          <div v-if="heroContentTitle || ctaLinks"
               :style="{ 'margin-bottom': isVideoContent && isMobile ? '6rem' : '' }"
               :class="`hero-single-content__flex-info ${ itemsAlignmentValue } ${ contentActive } ${ controlsActive }`">
            <div class="info-container">
              <h2 class="title">{{ heroContentTitle }}</h2>
              <div class="hero-text">{{ heroContentText }}</div>
              <div v-if="ctaLinks" class="cta-links">
                <template v-for="ctaObj in ctaLinks" >
                  <advanced-button-wrapper
                      :class="{ 'hero-content-cta-link full-width': ctaFullWidth, 'hero-content-cta-link': !ctaFullWidth }"
                      :key="ctaObj.uuid"
                      :cta-obj="ctaObj"
                      color="primary"
                      styleType="text"
                      size="medium"
                      :analyticsData="analyticsData(ctaObj)" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PgImage from "../Image/Image.vue";
import {vueVimeoPlayer} from 'vue-vimeo-player'
import advancedButtonWrapper from "../Advanced-Button-Wrapper/Advanced-Button-Wrapper";
import {mgnlComponentMixin} from "@wl-vue-mixins/mixins";

export default {
  mixins: [mgnlComponentMixin], // !!! all the mgnl components must be configured with mgnlComponentMixin !!!
  name: "HeroSingleContent",

  components: {
    advancedButtonWrapper,
    PgImage,
    vueVimeoPlayer
  },

  data() {
    return {
      isVideoPlaying: true,
      ctaFullWidth: false,
      videoSource: "",
      vimeoVideoSource: "",
      aspectRatio: "",
      itemsAlignmentValue: "",
      headerClassValue: "",
      vimeoPlayerClass: "",
      options: {
        muted: true,
        loop: true,
        autopause: false,
        responsive: true
      }
    }
  },

  props: {
    overlay : {
      type: String
    },
    itemsAlignment: {
      type: String,
      default: ''
    },
    heroContentTitle: {
      type: String,
      default: ''
    },
    heroContentText: {
      type: String,
      default: ''
    },
    ctaLinks: {
      type: Array,
      default: () => []
    },
    media: {
      type: Object,
      default: () => {}
    },
    mediaMobile: {
      type: Object,
      default: () => {}
    },
    vimeoControls: {
      type: Boolean,
      default: false
    },
    autoplayVideo: {
      type: Boolean,
      default: false,
    },
    mobileAspectRatio: {
      type: Boolean,
      default: false,
    },
    headerHeight: {
      type: String,
      default: ''
    },
    mobileFullCta: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isMobile() {
      return window.matchMedia('only screen and (max-width: 640px)').matches;
    },
    isLandscape() {
      return window.matchMedia('only screen and (max-width: 1024px) and (min-width: 641px)').matches;
    },
    isLandScapeWide(){
      //cover chromebook case
      return window.matchMedia('only screen and (width: 960px) and (height: 600px)').matches;
    },
    isHeroContentImage() {
      return this.mediaMobile?.type === 'image' || this.media?.type === 'image';
    },
    imageAlt() {
      return this.isMobile && this.mediaMobile?.media_name ? this.mediaMobile?.media_name : this.media.media_name;
    },
    isVideoContent() {
      return this.media?.type === 'document' || this.media?.type === 'externalvideo';
    },
    isMgnlVideo() {
      return this.media?.type === 'document' || this.mediaMobile?.type === 'document';
    },
    isVimeoVideo() {
      return this.media?.type === 'externalvideo' || this.mediaMobile?.type === 'externalvideo';
    },
    controlsActive() {
      return this.vimeoControls ? 'video-controls' : '';
    },
    contentActive() {
      return this.isVideoContent ? 'video-active' : 'image-active'
    },
    previewImage() {
      let preview;
      if (this.$mq === "small") {
        preview = this.mediaMobile?.previewImage !== "undefined" ? this.mediaMobile?.previewImage : item | this.$options.filters.mgnlMedia('url')
      } else {
        preview = this.media?.previewImage !== "undefined" ? this.media.previewImage : item | this.$options.filters.mgnlMedia('url');
      }

      this.preloadPosterImage(preview);
      // return `background:url(${preview}) transparent 0 0; background-size: cover;`;
      return `${preview}`;
    }
  },

  created() {
    this.setAspectRatio();
    this.setItemsAlignment();
    this.setCtaFullWidth();
    this.setHeaderClassValue();
    this.setVimeoPlayerClass();
    this.isVideoContent && this.setVideoSource();
  },

  mounted() {
    this.isVideoContent && this.setVideoSource();
    window.addEventListener("resize", this.setAspectRatio);
    window.addEventListener("resize", this.setItemsAlignment);
    window.addEventListener("resize", this.setCtaFullWidth);
    window.addEventListener("resize", this.setHeaderClassValue);
    window.addEventListener("resize", this.setVimeoPlayerClass);
    this.isVideoContent && window.addEventListener("resize", this.setVideoSource);
  },

  methods: {
    preloadPosterImage(imageUrl) {
      if (!imageUrl) return;
      const preloadLink = document.createElement('link');
      preloadLink.as = 'image';
      preloadLink.href = imageUrl;
      preloadLink.rel = 'preload';
      preloadLink.onload = () => {
        console.log('Image preloaded');
      };

      // Append the preload link to the document head
      document.head.appendChild(preloadLink);
    },
    setAspectRatio() {
      let isMobile = window.matchMedia('only screen and (max-width: 640px)').matches;
      let isLandscape = window.matchMedia('only screen and (max-width: 1024px) and (min-width: 641px)').matches;
      this.aspectRatio = isMobile && this.mobileAspectRatio && !this.heroContentTitle && this.ctaLinks.length === 0 ? "--aspect-ratio: 4/3;"
                          : this.isLandScapeWide ? "--aspect-ratio: 21/9;"
                          : isMobile ? "--aspect-ratio: 2/3;"
                          : isLandscape ? "--aspect-ratio: 4/3;"
                          : "--aspect-ratio: 21/9;";
    },
    setCtaFullWidth() {
      const isMobile = window.matchMedia('only screen and (max-width: 640px)').matches;
      this.ctaFullWidth = isMobile && this.mobileFullCta
    },
    setHeaderClassValue() {
      this.headerClassValue = 'section hero-single-content';
      if(this.headerHeight === '100svh')
        this.headerClassValue += ' hero-single-content-100'
      else if(this.headerHeight === '75svh')
        this.headerClassValue += ' hero-single-content-75'
    },
    setVimeoPlayerClass() {
      if(this.headerHeight === '100svh')
        this.vimeoPlayerClass = 'vimeo-player-content-100'
      else if(this.headerHeight === '75svh')
        this.vimeoPlayerClass = 'vimeo-player-content-75'
      else
        this.vimeoPlayerClass = 'vue-vimeo-player'
    },
    setItemsAlignment() {
      const isMobile = window.matchMedia('only screen and (max-width: 640px)').matches;
      this.itemsAlignmentValue = isMobile ? 'center' : this.itemsAlignment;
    },
    toggleVideoPlayback() {
      const video = this.$refs.videoPlayer;
      if (this.isVideoPlaying) {
        video.pause();
      } else {
        video.play();
      }
      this.isVideoPlaying = !this.isVideoPlaying;
    },
    setVideoSource() {
      let isMobile = window.matchMedia('only screen and (max-width: 640px)').matches;
      if(this.isVimeoVideo) {
        this.vimeoVideoSource = isMobile && (!this.mediaMobile || !this.mediaMobile.url) ? this.media.url : isMobile ? this.mediaMobile.url : this.media.url
      }else {
        this.videoSource = isMobile && (!this.mediaMobile || !this.mediaMobile.url) ? this.media.url : isMobile ? this.mediaMobile.url : this.media.url
      }
    },
    handlePlayerError(err) {
      if(err) {
        const iFrame = this.$refs.videoPlayer.$el.getElementsByTagName('iframe');
        if(iFrame.length > 0) {
          iFrame[0].style.display = 'none';
        }
      }
    },
    analyticsData(el) {
      return {
        evCategory: 'call_to_action',
        evAction: el.document?.url || el.link?.url || el.trackingLabel,
        evLabel: `${el.campaignId || 'No Campaign'} | ${this.common.actpageFullUrl}`
      }
    }
  }
}

</script>